import { useEffect, useMemo, useState, Component, type ReactNode, type ErrorInfo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { Minus, Plus, ShoppingBasket, CheckCircle, AlertCircle, Calendar, Clock } from 'lucide-react';

// Error boundary to catch in-app browser JS errors that would otherwise show a white screen
class TestOrderErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; errorMsg: string }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, errorMsg: '' };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorMsg: error.message || String(error) };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[TestOrderErrorBoundary]', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFFBF5] p-6 text-center">
          <AlertCircle size={64} className="text-red-600 mb-4" />
          <p className="text-2xl font-bold text-stone-800 mb-4">頁面載入失敗</p>
          <p className="text-lg text-stone-600 mb-6">請用系統瀏覽器（Safari / Chrome）開啟，或稍後再試。</p>
          <p className="text-sm text-stone-500 break-all max-w-xs">{this.state.errorMsg}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

const HERO_IMAGE = 'https://shop.deliciousexpress.com.hk/media/volunteer/%E5%A5%BD%E5%A3%AB%E5%A4%9A_hero.jpg';
const TIME_SLOTS = ['06:00-08:00', '10:00-12:00'];
const CATEGORY_ORDER = ['餸包', '蔬菜', '水果'];
const DATE_OPTIONS_COUNT = 5;

function formatHkDate(date: Date): string {
  return date.toLocaleDateString('zh-HK', {
    timeZone: 'Asia/Hong_Kong',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  });
}

function formatHkDateShort(date: Date): { date: string; weekday: string } {
  const parts = date.toLocaleDateString('zh-HK', {
    timeZone: 'Asia/Hong_Kong',
    month: 'long',
    day: 'numeric',
    weekday: 'short',
  }).split(' ');
  // zh-HK usually returns "5日 星期二" or "5日 週二"
  const weekday = parts.pop() || '';
  const dateText = parts.join(' ') || '';
  return { date: dateText, weekday: weekday.replace(/星期?/, '') };
}

function getUpcomingHkDates(count: number): Date[] {
  const dates: Date[] = [];
  const base = new Date();
  for (let i = 1; i <= count; i++) {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    dates.push(d);
  }
  return dates;
}
const CATEGORY_MAP: Record<string, string> = {
  item: '餸包',
  vegetable: '蔬菜',
  fruit: '水果',
};

interface Product {
  id: number;
  name: string;
  category: string;
  price: number;
  stockQuantity: number;
  imageUrl?: string | null;
}

interface CartItem {
  productId: number;
  name: string;
  unit: string;
  price: number;
  quantity: number;
}

function extractUnit(name: string): string {
  const parts = name.trim().split(/\s+/);
  const last = parts[parts.length - 1] || '';
  if (/[份個包扎盒斤兩條棵罐支袋]/.test(last)) return last;
  return '份';
}

function cleanName(name: string): string {
  const parts = name.trim().split(/\s+/);
  const last = parts[parts.length - 1] || '';
  if (/[份個包扎盒斤兩條棵罐支袋]/.test(last)) {
    return parts.slice(0, -1).join(' ') || name;
  }
  return name;
}

function TestOrderContent() {
  const [searchParams] = useSearchParams();
  const phone = searchParams.get('phone') || '';

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('餸包');
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [deliveryDate, setDeliveryDate] = useState<string>(() => formatHkDate(getUpcomingHkDates(1)[0]));
  const [deliveryTime, setDeliveryTime] = useState(TIME_SLOTS[0]);
  const [showSummary, setShowSummary] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [showExitModal, setShowExitModal] = useState(false);

  useEffect(() => {
    let mounted = true;
    apiFetch('/api/public/products')
      .then(async (res) => {
        if (!res.ok) throw new Error('無法載入商品');
        const data = await res.json();
        if (!mounted) return;
        const list = (Array.isArray(data) ? data : data.products || []).map((p: any) => ({
          id: p.id,
          name: String(p.name || ''),
          category: String(p.category || ''),
          price: Number(p.price || 0),
          stockQuantity: Number(p.stock_quantity || 0),
          imageUrl: p.image_url,
        })) as Product[];
        setProducts(list);
        const localMap: Record<string, Product[]> = {};
        CATEGORY_ORDER.forEach((c) => { localMap[c] = []; });
        list.forEach((p) => {
          const displayCategory = CATEGORY_MAP[p.category] || p.category;
          localMap[displayCategory] = localMap[displayCategory] || [];
          localMap[displayCategory].push(p);
        });
        const firstCategory = CATEGORY_ORDER.find((c) => (localMap[c] || []).length > 0) || CATEGORY_ORDER[0];
        setActiveCategory(firstCategory);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e.message || '載入失敗');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const grouped = useMemo(() => {
    const map: Record<string, Product[]> = {};
    CATEGORY_ORDER.forEach((c) => { map[c] = []; });
    products.forEach((p) => {
      const displayCategory = CATEGORY_MAP[p.category] || p.category;
      map[displayCategory] = map[displayCategory] || [];
      map[displayCategory].push(p);
    });
    return map;
  }, [products]);

  const cartItems: CartItem[] = useMemo(() => {
    return Object.entries(quantities)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => {
        const p = products.find((x) => x.id === Number(id));
        if (!p) return null;
        return {
          productId: p.id,
          name: cleanName(p.name),
          unit: extractUnit(p.name),
          price: p.price,
          quantity: qty,
        };
      })
      .filter(Boolean) as CartItem[];
  }, [quantities, products]);

  const totalQty = cartItems.reduce((sum, it) => sum + it.quantity, 0);
  const totalAmount = cartItems.reduce((sum, it) => sum + it.price * it.quantity, 0);
  const availableDates = useMemo(() => getUpcomingHkDates(DATE_OPTIONS_COUNT), []);

  const setQty = (id: number, delta: number) => {
    setQuantities((prev) => {
      const current = prev[id] || 0;
      const next = Math.max(0, current + delta);
      if (next === 0) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: next };
    });
  };

  const handleSubmit = async () => {
    if (!phone || totalQty === 0) return;
    setSubmitting(true);
    try {
      const res = await apiFetch('/api/public/test-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify({
          phone,
          deliveryDate,
          deliveryTime,
          items: cartItems,
          totalAmount,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSubmitResult({ ok: true, message: '我們已收到你的訂單，請留意 WhatsApp 確認訊息。' });
        setQuantities({});
      } else {
        setSubmitResult({ ok: false, message: data.error || '提交失敗，請稍後再試' });
      }
    } catch (e: any) {
      setSubmitResult({ ok: false, message: e.message || '網絡錯誤' });
    } finally {
      setSubmitting(false);
      setShowSummary(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFBF5]">
        <p className="text-2xl font-bold text-stone-600">載入中…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFFBF5] p-6 text-center">
        <AlertCircle size={48} className="text-red-600 mb-4" />
        <p className="text-2xl font-bold text-stone-800 mb-2">載入失敗</p>
        <p className="text-lg text-stone-600">{error}</p>
      </div>
    );
  }

  if (submitResult) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFFBF5] p-6 text-center">
        {submitResult.ok ? (
          <CheckCircle size={64} className="text-green-600 mb-4" />
        ) : (
          <AlertCircle size={64} className="text-red-600 mb-4" />
        )}
        <p className={`text-3xl font-bold mb-4 ${submitResult.ok ? 'text-green-700' : 'text-red-700'}`}>
          {submitResult.ok ? '提交成功' : '提交失敗'}
        </p>
        <p className="text-xl text-stone-700 leading-relaxed max-w-sm mb-8">{submitResult.message}</p>
        <button
          type="button"
          onClick={() => {
            // Web pages cannot force-close an in-app browser window.
            // Show a clear modal telling the user to tap the native X button,
            // then auto-dismiss after 3s and return to the order form.
            setShowExitModal(true);
            // Also try to deep-link back to WhatsApp; on some devices this works.
            if (/WhatsApp/i.test(navigator.userAgent)) {
              window.location.href = 'https://wa.me/';
            } else {
              window.close();
            }
            setTimeout(() => {
              setShowExitModal(false);
              setSubmitResult(null);
            }, 3000);
          }}
          className="px-8 py-4 rounded-full text-xl font-bold bg-[#C2410C] text-white hover:bg-[#9a3412] transition shadow-lg"
        >
          完成並返回 WhatsApp
        </button>

        {/* Exit helper modal */}
        {showExitModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center shadow-2xl">
              <p className="text-2xl font-bold text-stone-800 mb-2">請按畫面左上角的</p>
              <p className="text-4xl font-bold text-stone-800 mb-4">×</p>
              <p className="text-xl text-stone-600">關閉視窗回到 WhatsApp</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5] text-stone-800 pb-28">
      {/* Hero */}
      <div className="relative h-48 sm:h-56 overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt="好士多"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40 flex items-end">
          <div className="w-full px-4 pb-4">
            <h1 className="text-3xl sm:text-4xl font-bold text-white drop-shadow-lg">好士多 測試下單</h1>
          </div>
        </div>
      </div>

      {/* Delivery info */}
      <div className="bg-white border-b border-orange-100 px-4 py-5">
        <div className="mb-5">
          <div className="flex items-center gap-3 mb-3">
            <Calendar size={28} className="text-[#C2410C]" />
            <p className="text-sm text-stone-500 font-medium">選擇送貨日期（預設明天）</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {availableDates.map((d) => {
              const label = formatHkDate(d);
              const isSelected = deliveryDate === label;
              const { date, weekday } = formatHkDateShort(d);
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => setDeliveryDate(label)}
                  className={`px-3 py-3 rounded-xl text-lg font-bold border-2 transition text-center leading-tight ${
                    isSelected
                      ? 'bg-[#C2410C] text-white border-[#C2410C]'
                      : 'bg-white text-stone-700 border-stone-300 hover:border-[#C2410C]'
                  }`}
                >
                  <span className="block">{date}</span>
                  <span className={`block text-sm font-normal ${isSelected ? 'text-orange-100' : 'text-stone-400'}`}>（{weekday}）</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Clock size={28} className="text-[#C2410C]" />
          <div className="flex-1">
            <p className="text-sm text-stone-500 font-medium">選擇送貨時段</p>
            <div className="flex flex-wrap gap-3 mt-2">
              {TIME_SLOTS.map((slot) => (
                <button
                  key={slot}
                  type="button"
                  onClick={() => setDeliveryTime(slot)}
                  className={`px-4 py-2 rounded-full text-lg font-bold border-2 transition ${
                    deliveryTime === slot
                      ? 'bg-[#C2410C] text-white border-[#C2410C]'
                      : 'bg-white text-stone-700 border-stone-300 hover:border-[#C2410C]'
                  }`}
                >
                  {slot}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Category tabs */}
      <div className="sticky top-0 z-30 bg-[#FFFBF5] border-b border-orange-100 px-2 py-3">
        <div className="flex gap-2 overflow-x-auto">
          {CATEGORY_ORDER.map((cat) => {
            const count = grouped[cat]?.length || 0;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                disabled={count === 0}
                className={`flex-shrink-0 px-5 py-3 rounded-full text-xl font-bold border-2 transition ${
                  activeCategory === cat
                    ? 'bg-[#C2410C] text-white border-[#C2410C]'
                    : count === 0
                      ? 'bg-stone-100 text-stone-400 border-stone-200 cursor-not-allowed'
                      : 'bg-white text-stone-700 border-stone-300 hover:border-[#C2410C]'
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      {/* Product list */}
      <div className="px-4 py-5 space-y-4">
        {(grouped[activeCategory] || []).map((p) => {
          const qty = quantities[p.id] || 0;
          return (
            <div
              key={p.id}
              className="bg-white rounded-2xl p-4 border border-orange-100 shadow-sm flex flex-col sm:flex-row gap-4"
            >
              {p.imageUrl ? (
                <img
                  src={p.imageUrl}
                  alt={p.name}
                  className="w-full h-40 sm:w-28 sm:h-28 rounded-xl object-cover bg-stone-100 flex-shrink-0"
                />
              ) : (
                <div className="w-full h-40 sm:w-28 sm:h-28 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                  <ShoppingBasket size={40} className="text-[#C2410C]" />
                </div>
              )}
              <div className="flex-1 min-w-0 flex flex-col gap-1">
                <p className="text-2xl font-bold text-stone-800 leading-tight">{cleanName(p.name)}</p>
                <p className="text-xl font-bold text-[#C2410C]">HK${p.price} / {extractUnit(p.name)}</p>
              </div>
              <div className="flex flex-row sm:flex-col items-center justify-end sm:justify-center gap-3 sm:gap-2 self-stretch sm:self-center">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setQty(p.id, -1)}
                    disabled={qty === 0}
                    className="w-12 h-12 rounded-full bg-stone-100 text-stone-700 disabled:text-stone-300 disabled:bg-stone-50 flex items-center justify-center text-2xl font-bold active:bg-orange-100"
                    aria-label="減少"
                  >
                    <Minus size={24} />
                  </button>
                  <span className="w-10 text-center text-2xl font-bold text-[#C2410C]">{qty}</span>
                  <button
                    type="button"
                    onClick={() => setQty(p.id, 1)}
                    className="w-12 h-12 rounded-full bg-[#C2410C] text-white flex items-center justify-center text-2xl font-bold active:bg-[#9a3412]"
                    aria-label="增加"
                  >
                    <Plus size={24} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {(grouped[activeCategory] || []).length === 0 && (
          <p className="text-center text-xl text-stone-500 py-10">此分類暫無商品</p>
        )}
      </div>

      {/* Bottom order bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-orange-200 px-4 py-4 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] z-40">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-stone-500">已選 {totalQty} 件</p>
            <p className="text-3xl font-bold text-[#C2410C]">HK${totalAmount}</p>
          </div>
          <button
            type="button"
            onClick={() => setShowSummary(true)}
            disabled={totalQty === 0}
            className={`px-8 py-4 rounded-full text-xl font-bold shadow-lg transition ${
              totalQty > 0
                ? 'bg-[#C2410C] text-white hover:bg-[#9a3412]'
                : 'bg-stone-200 text-stone-400 cursor-not-allowed'
            }`}
          >
            下單
          </button>
        </div>
      </div>

      {/* Summary modal */}
      {showSummary && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowSummary(false)} />
          <div className="relative bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col overflow-hidden shadow-2xl">
            <div className="px-5 py-4 border-b border-orange-100 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-stone-800">訂單總匯</h2>
              <button
                type="button"
                onClick={() => setShowSummary(false)}
                className="text-stone-400 text-3xl leading-none px-2"
                aria-label="關閉"
              >
                ×
              </button>
            </div>
            <div className="p-5 overflow-y-auto space-y-4">
              <div className="bg-orange-50 rounded-xl p-4">
                <p className="text-lg font-bold text-stone-700">送貨日期：{deliveryDate}</p>
                <p className="text-lg font-bold text-stone-700">送貨時段：{deliveryTime}</p>
              </div>
              {cartItems.map((it) => (
                <div key={it.productId} className="flex items-center justify-between border-b border-stone-100 pb-3">
                  <div>
                    <p className="text-xl font-bold text-stone-800">{it.name}</p>
                    <p className="text-base text-stone-500">{it.quantity} {it.unit} · HK${it.price}/{it.unit}</p>
                  </div>
                  <p className="text-xl font-bold text-[#C2410C]">HK${it.price * it.quantity}</p>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2">
                <p className="text-2xl font-bold text-stone-800">總計</p>
                <p className="text-3xl font-bold text-[#C2410C]">HK${totalAmount}</p>
              </div>
            </div>
            <div className="p-5 border-t border-orange-100">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full py-4 rounded-full text-xl font-bold bg-[#C2410C] text-white hover:bg-[#9a3412] disabled:bg-stone-300 transition"
              >
                {submitting ? '提交中…' : '確定下單'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TestOrder() {
  return (
    <TestOrderErrorBoundary>
      <TestOrderContent />
    </TestOrderErrorBoundary>
  );
}
